import { useReducer } from 'react';

import { selectCanSend, selectIsLoading } from './chat.selectors';
import type {
  ChatMachineAction,
  ChatMachineState,
  Message,
} from './chat.types';

export const initialChatMachineState: ChatMachineState = {
  status: 'idle',
  inputValue: '',
  messages: [],
  errorMessage: null,
  isThinking: false,
  isStreaming: false,
  activeAgentMessageId: null,
  activeStreamEventSeq: 0,
};

function appendMessage(messages: Message[], message: Message): Message[] {
  return [...messages, message];
}

function attachRecommendations(
  messages: Message[],
  id: string,
  recommendations: NonNullable<Message['recommendations']>,
): Message[] {
  const index = messages.findIndex((message) => message.id === id);
  if (index < 0) return messages;

  const message = messages[index];
  if (message?.recommendations) return messages;

  return messages.map((entry, entryIndex) =>
    entryIndex === index
      ? {
          ...entry,
          recommendations,
        }
      : entry,
  );
}

// Invariant: Streaming messages update in-place by ID to preserve scroll position.
// New messages append to end; existing messages update to show incremental text.
function upsertMessage(messages: Message[], message: Message): Message[] {
  const index = messages.findIndex((m) => m.id === message.id);
  if (index < 0) return appendMessage(messages, message);
  const existing = messages[index];
  const mergedMessage = existing
    ? {
        ...existing,
        ...message,
        recommendations: message.recommendations ?? existing.recommendations,
      }
    : message;
  return messages.map((m) => (m.id === message.id ? mergedMessage : m));
}

export function chatMachineReducer(
  state: ChatMachineState,
  action: ChatMachineAction,
): ChatMachineState {
  switch (action.type) {
    case 'SET_INPUT': {
      return { ...state, inputValue: action.value };
    }

    case 'CONNECTING': {
      return {
        ...state,
        status: 'connecting',
        errorMessage: null,
      };
    }

    case 'CONNECTED': {
      return {
        ...state,
        status: 'connected',
      };
    }

    case 'CONNECT_FAILED': {
      return {
        ...state,
        status: 'error',
        errorMessage: action.errorMessage,
        isThinking: false,
        isStreaming: false,
        activeAgentMessageId: null,
        activeStreamEventSeq: 0,
      };
    }

    case 'USER_MESSAGE_SENT': {
      const newMessage: Message = {
        id: action.id,
        isUser: true,
        text: action.text,
      };
      return {
        ...state,
        status: 'sending',
        inputValue: '',
        messages: appendMessage(state.messages, newMessage),
        isThinking: true,
        isStreaming: false,
        activeAgentMessageId: null,
        activeStreamEventSeq: 0,
        errorMessage: null,
      };
    }

    case 'AGENT_MESSAGE_RECEIVED': {
      const newMessage: Message = {
        id: action.id,
        isUser: false,
        text: action.text,
      };
      return {
        ...state,
        status: 'connected',
        messages: appendMessage(state.messages, newMessage),
        isThinking: false,
        isStreaming: false,
        activeAgentMessageId: null,
        activeStreamEventSeq: 0,
      };
    }

    case 'AGENT_MESSAGE_STREAMED': {
      const newMessage: Message = {
        id: action.id,
        isUser: false,
        text: action.text,
      };
      // Business rule: Stream sequence increments only for same message ID.
      // New message ID resets sequence to 1 to track separate streams.
      const nextSeq =
        state.activeAgentMessageId === action.id
          ? state.activeStreamEventSeq + 1
          : 1;
      return {
        ...state,
        status: 'connected',
        messages: upsertMessage(state.messages, newMessage),
        isThinking: false,
        isStreaming: true,
        activeAgentMessageId: action.id,
        activeStreamEventSeq: nextSeq,
      };
    }

    case 'AGENT_STREAM_COMPLETE': {
      // Invariant: Only complete stream matching active message ID.
      // Prevents race conditions from out-of-order stream completion events.
      if (state.activeAgentMessageId !== action.id) {
        return state;
      }

      return {
        ...state,
        isStreaming: false,
        activeAgentMessageId: null,
        activeStreamEventSeq: 0,
      };
    }

    case 'ATTACH_RECOMMENDATIONS': {
      return {
        ...state,
        messages: attachRecommendations(
          state.messages,
          action.id,
          action.recommendations,
        ),
      };
    }

    case 'SET_THINKING': {
      return { ...state, isThinking: action.isThinking };
    }

    case 'ERROR': {
      return {
        ...state,
        status: 'error',
        errorMessage: action.errorMessage,
        isThinking: false,
        isStreaming: false,
        activeAgentMessageId: null,
        activeStreamEventSeq: 0,
      };
    }

    case 'CLEAR_ERROR': {
      return {
        ...state,
        errorMessage: null,
      };
    }

    case 'RESET': {
      return initialChatMachineState;
    }

    default:
      return state;
  }
}

export function useChatMachine(initialMessages?: Message[]): {
  state: ChatMachineState;
  dispatch: React.Dispatch<ChatMachineAction>;
  canSend: boolean;
  isLoading: boolean;
} {
  // Workaround: useReducer lazy initialization pattern required to restore persisted state.
  // Without this, initialMessages would be ignored on subsequent renders after state hydration.
  const [state, dispatch] = useReducer(
    chatMachineReducer,
    initialMessages ?? null,
    (messages) => {
      if (!messages || messages.length === 0) {
        return initialChatMachineState;
      }
      return {
        ...initialChatMachineState,
        messages,
      };
    },
  );

  return {
    state,
    dispatch,
    canSend: selectCanSend(state),
    isLoading: selectIsLoading(state),
  };
}
